/**
 * R160h · Feb 2026 — Daily Completion Report settings dialog.
 * Workspace admins can edit recipients, timing and PDF-attach preferences.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Loader2, Mail, Clock } from "lucide-react";

export default function DailyReportSettingsDialog({ wsId, onClose }) {
  const [state, setState] = useState({
    enabled: false, recipients: [], include_workspace_owner: true,
    include_pdf: true, send_hour: 19, send_minute: 30,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rcptInput, setRcptInput] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/workspaces/${wsId}/reports/daily-completion/settings`);
        setState((prev) => ({ ...prev, ...data }));
        setRcptInput((data.recipients || []).join(", "));
      } catch (e) { toast.error(apiErrorMessage(e, "Couldn't load settings")); }
      finally { setLoading(false); }
    })();
  }, [wsId]);

  const save = async () => {
    setSaving(true);
    try {
      const recipients = rcptInput.split(/[,;\n]/).map((e) => e.trim()).filter((e) => e && e.includes("@"));
      const { data } = await api.patch(`/workspaces/${wsId}/reports/daily-completion/settings`, {
        ...state, recipients,
      });
      setState((prev) => ({ ...prev, ...data }));
      toast.success("Settings saved");
      onClose?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed — you need workspace-admin permission")); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="daily-report-settings-dialog">
        <DialogHeader>
          <DialogTitle>Daily Completion Report — Email Settings</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="py-8 text-center text-muted-foreground text-sm"><Loader2 className="w-4 h-4 mx-auto animate-spin mb-2" />Loading…</div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-3 border border-border rounded-md p-3">
              <div>
                <div className="text-sm font-semibold">Enable daily email</div>
                <p className="text-xs text-muted-foreground mt-1">Send this report automatically at the configured time.</p>
              </div>
              <Switch
                checked={!!state.enabled}
                onCheckedChange={(v) => setState((s) => ({ ...s, enabled: v }))}
                data-testid="settings-enabled"
              />
            </div>

            <div>
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Recipients</Label>
              <textarea
                value={rcptInput}
                onChange={(e) => setRcptInput(e.target.value)}
                rows={3}
                placeholder="alice@acme.com, bob@acme.com"
                className="w-full mt-1 px-3 py-2 text-sm border border-border rounded-md bg-background outline-none focus:ring-2 focus:ring-primary/30"
                data-testid="settings-recipients"
              />
              <div className="flex items-center gap-2 mt-2">
                <Switch
                  checked={!!state.include_workspace_owner}
                  onCheckedChange={(v) => setState((s) => ({ ...s, include_workspace_owner: v }))}
                  data-testid="settings-include-owner"
                />
                <span className="text-xs">Also send to the workspace owner</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Clock className="w-4 h-4 text-muted-foreground" />
              <div>
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Send at (workspace timezone)</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input type="number" min={0} max={23} value={state.send_hour}
                          onChange={(e) => setState((s) => ({ ...s, send_hour: Number(e.target.value) }))}
                          className="w-16 h-9" data-testid="settings-hour" />
                  <span>:</span>
                  <Input type="number" min={0} max={59} value={state.send_minute}
                          onChange={(e) => setState((s) => ({ ...s, send_minute: Number(e.target.value) }))}
                          className="w-16 h-9" data-testid="settings-minute" />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 border border-border rounded-md p-3">
              <Mail className="w-4 h-4 text-muted-foreground" />
              <div className="flex-1 text-sm">Attach PDF version</div>
              <Switch checked={!!state.include_pdf}
                       onCheckedChange={(v) => setState((s) => ({ ...s, include_pdf: v }))}
                       data-testid="settings-include-pdf" />
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || loading} data-testid="settings-save">
            {saving ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Saving…</> : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
